import React, { useContext, useState } from "react";
import PropTypes from "prop-types";

import { Container, Header, Loader } from "semantic-ui-react";

import { useNotify } from "data/messages/hooks";

import { FeatureFlag } from "components/FeatureFlag";
import SimpleLinePlaceholder from "components/SimpleLinePlaceholder";

import {
  useAccessTypeStore,
  useEditorData,
  useEditorState,
} from "scenes/Hubly/components/Workspace/components/hooks";
import MultiTasksAssigners from "scenes/Hubly/components/Workspace/components/MultiTasksAssigners";
import { HubContext } from "scenes/Hubly/components/Workspace/Provider";

import {
  Conditions,
  LegacyTaskCondition,
  TaskDetails,
  TaskEmailTemplate,
  TaskFutureReminder,
  TaskTimeEstimate,
} from "./components/Task/components";
import { DetailsModal } from "./components/Task/components/TaskDetails/components";
import { useConditionalItemOrderManager } from "../../hooks";
import { TaskAdd, TaskList, useReassignTasks } from "./components";

export function TasksEditor({ showAssignedAdvisor, workflow }) {
  const { fetchTasks } = useContext(HubContext);
  const { hasAccess } = useAccessTypeStore();
  const [taskGroups, dispatch] = useEditorState();
  const { loading, reloadData } = useEditorData(fetchTasks, {
    id: workflow.id,
    dispatch: dispatch,
  });
  const notifyNextWorkflows = useNotify("NEXT_WORKFLOWS");
  const [detailsModalProps, showDetailsProps] = useState(null);
  const { assign } = useReassignTasks(workflow, reloadData);
  const {
    running,
    lastOrder,
    reorder,
    items: tasks,
  } = useConditionalItemOrderManager(taskGroups, dispatch, reloadData, loading);

  if (loading && tasks.length === 0) {
    return (
      <Container className="configure_tasks">
        <Header as="h5">Default Tasks</Header>
        <SimpleLinePlaceholder numLines={workflow.numTasks || 4} />
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Container className="configure_tasks">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Header as="h5">Default Tasks</Header>
          <Loader
            size="tiny"
            inline
            active={running}
            style={{ marginRight: "0.3em" }}
          />
        </div>
        <FeatureFlag id="bulk-reassign-workflow-template-tasks">
          <MultiTasksAssigners
            disabled={!hasAccess()}
            style={{ marginRight: "10px", marginBottom: "2em" }}
            tasks={tasks}
            onAssign={assign}
            badge={(t) => {
              return t;
            }}
          />
        </FeatureFlag>
        <Container className="task_list" style={{ marginBottom: "0.5em" }}>
          <TaskList
            workflowId={workflow.id}
            taskGroups={taskGroups}
            disableDnd={running}
            onTaskUpdated={(
              updatedTask,
              type = "edit",
              shouldReload = true
            ) => {
              dispatch({ type: type, value: updatedTask });
              if (shouldReload) reloadData();
            }}
            onTaskReOrdered={reorder}
            onTaskDeleted={(deletedTaskId) => {
              dispatch({ type: "remove", value: deletedTaskId });
              notifyNextWorkflows([true]);
              reloadData();
            }}
            showAssignedAdvisor={showAssignedAdvisor}
          />
        </Container>
        {workflow?.id && (
          <TaskAdd
            lastOrder={lastOrder}
            showAssignedAdvisor={showAssignedAdvisor}
            workflowId={workflow.id}
            onTaskAdded={(addedTask) => {
              dispatch({ type: "add", value: addedTask });
              reloadData();
            }}
          >
            {(p) => {
              const { task: t, onTaskConfigChange } = p;
              return (
                <React.Fragment>
                  <TaskFutureReminder numTasks={tasks.length} {...p} />
                  <TaskEmailTemplate {...p} />
                  <TaskTimeEstimate {...p} />
                  <TaskDetails
                    {...p}
                    onShowDetailProps={(detailProps) => {
                      showDetailsProps(detailProps);
                    }}
                  >
                    {(taskProps) => {
                      return (
                        detailsModalProps && (
                          <DetailsModal {...detailsModalProps} {...taskProps} />
                        )
                      );
                    }}
                  </TaskDetails>
                  <FeatureFlag id="conditional-logic">
                    <Conditions
                      workflowId={workflow.id}
                      item={t}
                      onConfigChange={onTaskConfigChange}
                    />
                  </FeatureFlag>
                  <FeatureFlag id="conditional-logic" showOnDisabled>
                    <LegacyTaskCondition {...p} />
                  </FeatureFlag>
                </React.Fragment>
              );
            }}
          </TaskAdd>
        )}
      </Container>
    </React.Fragment>
  );
}

TasksEditor.propTypes = {
  workflow: PropTypes.object,
  showAssignedAdvisor: PropTypes.bool,
};

TasksEditor.defaultProps = {
  showAssignedAdvisor: false,
  workflow: null,
};

export default TasksEditor;
